'use client'

import { Button } from '@/components/Button/Button';
import { getDiscoveryCoveTicketWithDates } from '@/lib/getTickets';
import useCartStore from '@/store/useCartStore';
import { AddToCartTicketType } from '@/types/CartType';
import { CalendarTicketAvailability, ProductType } from '@/types/TicketType';
import { getGeneralTicketId } from '@/utils/ticketUtils';
import { addMonths, differenceInMonths, endOfMonth, format, startOfMonth } from 'date-fns';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import Calendar, { CalendarTileProperties, ViewCallback } from 'react-calendar';
import parse from 'html-react-parser';
import Popup from 'reactjs-popup';
import { AddtoCartForm } from '../AddtoCartForm/AddtoCartForm';
import { DropDownWithPrice } from '../AddtoCartForm/DropDownWithPrice/DropDownWithPrice';
import styles from './TicketPopUp.module.scss';

type TicketPopUpProps = {
  ticket: ProductType;
  name: String;
}

async function loadAvailability(
  ticket_id: string,
  date: Date,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setAvailability: Function,
  setSelectedDate: Dispatch<SetStateAction<Date>>,
  setSelectedDatePrice: Dispatch<SetStateAction<string>>
) {
  setLoading(true);

  const numberOfMonthsToFetch = 3;

  // Updated dateFrom and dateTo to include months to fetch backwards
  const dateFrom = format(addMonths(startOfMonth(date), -numberOfMonthsToFetch), 'yyyy-MM-dd');
  const dateTo = format(endOfMonth(addMonths(startOfMonth(date), numberOfMonthsToFetch)), 'yyyy-MM-dd');

  const tickets = await getDiscoveryCoveTicketWithDates(ticket_id, dateFrom, dateTo);

  if (tickets) {
    setAvailability(tickets.tickets[0].availability);

    const firstAvailableDate =
      tickets.tickets[0].availability?.find(
        (avail) => avail.date >= new Date().toISOString().substring(0, 10),
      ) ?? null;

    if (firstAvailableDate) {
      setSelectedDate(new Date(firstAvailableDate.date));
      setSelectedDatePrice(firstAvailableDate.price);
    }
  } else {
    setAvailability([]);
  }

  setLoading(false);
}

function getAvailabilityForDate(availability: CalendarTicketAvailability[], date: Date) {
  return availability.find((a => a.date === format(date, 'yyyy-MM-dd')))
}

export function TicketPopUp({ ticket, name }: TicketPopUpProps) {

  const [showDetails, setShowDetails] = useState(false);

  const handleToggleClick = (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    setShowDetails(!showDetails);
  };

  const addtoCart = useCartStore(state => state.addToCart)
  const cartLoading = useCartStore(state => state.cartLoading)

  const [open, setOpen] = useState<boolean>(false)
  const [qty, setQty] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(cartLoading)
  const [availability, setAvailability] : [ availability: CalendarTicketAvailability[], setAvailability: Function ] = useState([])
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedDatePrice, setSelectedDatePrice] = useState<string>('0')
  const generalTicketId = getGeneralTicketId(ticket)
  const generalTicket = generalTicketId ? ticket.tickets.find(t => t.ticket_id === generalTicketId) : null

  const calendarDiv = useRef<HTMLInputElement>(null)

  const getTileDisabled = ({ date }: CalendarTileProperties): boolean => {
    const avail = getAvailabilityForDate(availability, date)
    return avail ? false : true
  }

  const getTileContent = ({ date }: CalendarTileProperties): JSX.Element => {
    const avail = getAvailabilityForDate(availability, date)
    return avail ? <div className='dayPrice'>£{ avail.price }</div> : <></>
  }

  const onChangeMonth: ViewCallback = ({ activeStartDate }) => {
    if (generalTicket) loadAvailability(generalTicket.product_id, activeStartDate, setLoading, setAvailability, setSelectedDate, setSelectedDatePrice)
  }

  const selectedDateId = getAvailabilityForDate(availability, selectedDate)?.date_id

  async function addToCartButton(dateId: string, callback?: Function) {

    setLoading(true)

    const items: Array<AddToCartTicketType> = []
    if (generalTicket) items.push({ ticketId: generalTicket.ticket_id, dateId, qty: qty})
    if (items.length) await addtoCart(items)
    if (callback) callback()
    setQty(0)
    setOpen(false)

    setLoading(false)
  }

  useEffect(() => {
    if (open) {
      const startDate = new Date()
      if (generalTicket) loadAvailability(generalTicket.product_id, startDate, setLoading, setAvailability, setSelectedDate, setSelectedDatePrice,)
    }
  },[open, generalTicket,])


  useEffect(() => {
    const avail = getAvailabilityForDate(availability, selectedDate)
    if (avail) {
      setSelectedDatePrice(avail.price)
    }
  }, [selectedDate, availability])

  return (
    <>
      <button aria-label={`ticket - ${ticket.title}`}  className={styles.modalLink} onClick={() => setOpen(true)}>
        View Details
      </button>
      <Popup modal lockScroll open={open} onClose={() => setOpen(false)}>
        <div className={styles.modal}>
          <Button ariaLabel='close' onClick={() => setOpen(false)} className={styles.close}>
            <span className={styles.visuallyHidden}>Close</span>
          </Button>

          <h2>{ticket.title}</h2>

          <div className={styles.popupBody}>
            <div className={styles.modalInfo}>
              <div>
                <>
                  {parse(ticket.desc)}
                </>
              </div>
              <h4>
                <a className={`${styles.toggle} ${showDetails ? styles.open : ''}`} href="#na" onClick={handleToggleClick}>Conditions</a>
              </h4>
              {showDetails === true && parse(ticket.conditions) }
            </div>

            <div className={styles.modalForm}>
              {!generalTicket ?
              <AddtoCartForm ticket={ticket} name={name} />
              :
              <>
                <Calendar
                  onChange={setSelectedDate}
                  onActiveStartDateChange={onChangeMonth}
                  tileDisabled={getTileDisabled}
                  tileContent={getTileContent}
                  value={selectedDate}
                  minDate={new Date()}
                  minDetail="month"
                  maxDetail='month'
                  inputRef={calendarDiv}
                />
                <DropDownWithPrice qty={qty} setQty={setQty} ticketPrice={selectedDatePrice}/>
                {selectedDateId ?
                  <Button
                  className={styles.btn}
                  loading={loading}
                  disabled={qty < 1}
                  onClick={() => addToCartButton(selectedDateId, () => setOpen(false))}
                >
                  Add to Cart
                </Button>
                    :
                  null
                }
              </>
            }
            </div>
          </div>
        </div>
      </Popup>
    </>
  )
}
